import { jsx as _jsx } from "react/jsx-runtime";
import { platformAccessibilityPreferencesValueSchema, platformBrandingIdentityValueSchema, platformCoreCopyValueSchema, platformShellLayoutValueSchema, platformThemeTokensValueSchema, } from '@crest/platform-configuration/definitions';
import { createContext, useEffect, useContext, useMemo, useState, } from 'react';
import { usePlatformBootstrap } from '../runtime/platform-bootstrap-provider.js';
const fontFamilies = {
    system: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    inter: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    'source-sans-3': '"Source Sans 3", -apple-system, BlinkMacSystemFont, sans-serif',
    'atkinson-hyperlegible': '"Atkinson Hyperlegible", -apple-system, sans-serif',
};
const shadows = {
    none: 'none',
    subtle: '0 1px 3px rgb(15 23 42 / 0.10)',
    raised: '0 10px 28px rgb(15 23 42 / 0.16)',
};
const buttonCornerShapes = {
    square: 'square',
    rounded: 'round',
    pill: 'round',
};
export function platformThemeStyle(value) {
    const color = value.mode === 'dark' ? value.theme.darkColor : value.theme.color;
    const gap = value.theme.card.gapPx;
    const radius = value.theme.card.radiusPx;
    return {
        '--crest-color-canvas': color.canvas,
        '--crest-color-surface': color.surface,
        '--crest-color-surface-elevated': color.surfaceElevated,
        '--crest-color-text': color.text,
        '--crest-color-text-muted': color.textMuted,
        '--crest-color-border': color.border,
        '--crest-color-brand': color.brand,
        '--crest-color-brand-contrast': color.brandContrast,
        '--crest-color-success': color.success,
        '--crest-color-warning': color.warning,
        '--crest-color-danger': color.danger,
        '--crest-color-focus': color.focus,
        '--crest-color-attention': color.attention,
        '--crest-color-attention-contrast': color.attentionContrast,
        '--crest-color-accent': color.brand,
        '--crest-color-primary': color.brand,
        '--crest-color-surface-muted': color.surface,
        '--crest-color-surface-subtle': color.surface,
        '--crest-color-border-subtle': color.border,
        '--crest-color-info-border': color.brand,
        '--crest-color-success-text': color.success,
        '--crest-font-family': fontFamilies[value.theme.typography.bodyFontFamily],
        '--crest-font-family-body': fontFamilies[value.theme.typography.bodyFontFamily],
        '--crest-font-family-heading': fontFamilies[value.theme.typography.headingFontFamily],
        '--crest-font-family-mono': 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        '--crest-font-size-base': `${value.theme.typography.baseSizePx}px`,
        '--crest-font-size-sm': `${value.theme.typography.baseSizePx * 0.875}px`,
        '--crest-font-size-lg': `${value.theme.typography.baseSizePx * 1.125}px`,
        '--crest-font-weight-semibold': 600,
        '--crest-line-height': value.theme.typography.lineHeight,
        '--crest-heading-scale': value.theme.typography.headingScale,
        '--crest-card-radius': `${value.theme.card.radiusPx}px`,
        '--crest-card-padding': `${value.theme.card.paddingPx}px`,
        '--crest-card-gap': `${value.theme.card.gapPx}px`,
        '--crest-card-min-width': `${value.theme.card.minWidthPx}px`,
        '--crest-card-max-width': value.theme.card.maxWidthPx === null
            ? 'none'
            : `${value.theme.card.maxWidthPx}px`,
        '--crest-card-shadow': shadows[value.theme.card.elevation],
        '--crest-control-radius': `${value.theme.control.radiusPx}px`,
        '--crest-control-min-height': `${value.theme.control.minHeightPx}px`,
        '--crest-button-shape': buttonCornerShapes[value.theme.button.shape],
        '--crest-button-radius': `${value.theme.button.radiusPx}px`,
        '--crest-button-min-height': `${value.theme.button.minHeightPx}px`,
        '--crest-field-action-min-height': `${value.theme.button.fieldActionMinHeightPx}px`,
        '--crest-motion-duration': `${value.theme.motion.standardDurationMs}ms`,
        '--crest-motion-easing': value.theme.motion.easing,
        '--crest-radius-card': `${radius}px`,
        '--crest-radius-md': `${Math.round(radius * 0.75)}px`,
        '--crest-radius-sm': `${Math.round(radius * 0.5)}px`,
        '--crest-radius-pill': '999px',
        '--crest-space-1': `${Math.max(2, Math.round(gap * 0.25))}px`,
        '--crest-space-2': `${Math.max(4, Math.round(gap * 0.5))}px`,
        '--crest-space-3': `${gap}px`,
        '--crest-space-4': `${Math.round(gap * 1.5)}px`,
        '--crest-space-5': `${gap * 2}px`,
        '--crest-border-width-strong': '3px',
        '--crest-sidebar-width': `${value.layout.sidebarWidthPx}px`,
        '--crest-content-max-width': `${value.layout.contentMaxWidthPx}px`,
        '--crest-accessibility-contrast': value.accessibility.contrast,
        '--crest-accessibility-reduce-motion': String(value.accessibility.reduceMotion),
        '--crest-accessibility-underline-links': String(value.accessibility.underlineLinks),
        '--crest-accessibility-focus-indicator': value.accessibility.focusIndicator,
    };
}
const PlatformThemeContext = createContext(null);
export function platformThemeContextFromValues(values, systemPrefersDark = false, approvedBrandAssets = {}) {
    const theme = platformThemeTokensValueSchema.parse(values['platform.theme.tokens']);
    return {
        theme,
        mode: theme.defaultMode === 'system'
            ? (systemPrefersDark ? 'dark' : 'light')
            : theme.defaultMode,
        branding: platformBrandingIdentityValueSchema.parse(values['platform.branding.identity']),
        layout: platformShellLayoutValueSchema.parse(values['platform.layout.shell']),
        accessibility: platformAccessibilityPreferencesValueSchema.parse(values['platform.accessibility.preferences']),
        copy: platformCoreCopyValueSchema.parse(values['platform.copy.core']),
        approvedBrandAssets,
    };
}
export function PlatformThemeProvider(props) {
    const bootstrap = usePlatformBootstrap();
    const [systemPrefersDark, setSystemPrefersDark] = useState(() => typeof window !== 'undefined' &&
        (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false));
    useEffect(() => {
        if (typeof window === 'undefined' || window.matchMedia === undefined)
            return undefined;
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const update = () => setSystemPrefersDark(media.matches);
        update();
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);
    const value = useMemo(() => {
        return platformThemeContextFromValues(bootstrap.configuration.values, systemPrefersDark, bootstrap.approvedBrandAssets);
    }, [bootstrap.approvedBrandAssets, bootstrap.configuration.values, systemPrefersDark]);
    return (_jsx(PlatformThemeContext, { value: value, children: _jsx("div", { className: "crest-theme", "data-color-mode": value.mode, "data-theme-preset": value.theme.presetId, "data-contrast": value.accessibility.contrast, "data-density": value.layout.density, "data-focus": value.accessibility.focusIndicator, "data-reduce-motion": String(value.accessibility.reduceMotion), "data-underline-links": String(value.accessibility.underlineLinks), style: platformThemeStyle(value), children: props.children }) }));
}
export function usePlatformTheme() {
    const value = useContext(PlatformThemeContext);
    if (value === null)
        throw new Error('PlatformThemeProvider is missing.');
    return value;
}
//# sourceMappingURL=platform-theme-provider.js.map